/**
 * Les bulles consécutives d'un même auteur se groupent : leurs coins se resserrent là où elles se
 * touchent, l'auteur ne s'écrit qu'au-dessus de la première, l'avatar ne se pose qu'une fois. Ce
 * calcul est pur : l'app le fait sur sa liste, puis donne à chaque `Bulle` sa `position`.
 *
 *   const groupes = grouperLesBulles(messages, { auteur: (m) => m.sender_key, date: (m) => m.created_at });
 *   groupes.map(({ item, position, debutDeGroupe }) => <Bulle key={item.id} position={position} auteur={debutDeGroupe ? nom : undefined} … />)
 *
 * Un groupe se coupe quand l'auteur change, quand plus de `ecartMax` sépare deux messages (cinq
 * minutes, comme les Spaces du cockpit), quand le jour change, et là où `coupure` le dit (un
 * séparateur « Nouveaux messages », un événement). Un auteur `null` (un événement système) est
 * toujours seul.
 */

export type PositionDansLeGroupe = "seule" | "premiere" | "milieu" | "derniere";

export interface OptionsDeGroupe<T> {
  /** La clé de l'auteur (le fan, Cyrano, l'id d'un chatteur). `null` : jamais groupé. */
  auteur: (item: T) => string | null;
  /** L'instant du message (ISO, ms, Date). Absent : seul l'auteur compte. */
  date?: (item: T) => string | number | Date;
  /** L'écart maximal entre deux messages d'un même groupe, en ms. Défaut 5 min. */
  ecartMax?: number;
  /** Coupe le groupe entre `precedent` et `item` (un séparateur posé entre eux). */
  coupure?: (precedent: T, item: T) => boolean;
  /** Le jour d'un instant, pour couper au changement de jour. Défaut : la date locale. */
  jour?: (instant: Date) => string;
}

export interface BulleGroupee<T> {
  item: T;
  position: PositionDansLeGroupe;
  /** La première du groupe : l'auteur s'écrit au-dessus, l'espace s'ouvre avant. */
  debutDeGroupe: boolean;
  /** La dernière du groupe : là où se pose un avatar « en bas ». */
  finDeGroupe: boolean;
}

const CINQ_MINUTES = 5 * 60_000;

function jourLocal(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

export function grouperLesBulles<T>(items: readonly T[], options: OptionsDeGroupe<T>): BulleGroupee<T>[] {
  const { auteur, date, ecartMax = CINQ_MINUTES, coupure, jour = jourLocal } = options;
  const instant = (item: T): Date | null => (date ? new Date(date(item)) : null);

  const memeGroupe = (a: T, b: T): boolean => {
    const ka = auteur(a);
    const kb = auteur(b);
    if (ka === null || kb === null || ka !== kb) return false;
    if (coupure?.(a, b)) return false;
    const da = instant(a);
    const db = instant(b);
    if (da && db && !Number.isNaN(da.getTime()) && !Number.isNaN(db.getTime())) {
      if (Math.abs(db.getTime() - da.getTime()) > ecartMax) return false;
      if (jour(da) !== jour(db)) return false;
    }
    return true;
  };

  return items.map((item, i) => {
    const avec = i > 0 && memeGroupe(items[i - 1]!, item);
    const apres = i < items.length - 1 && memeGroupe(item, items[i + 1]!);
    const position: PositionDansLeGroupe = avec ? (apres ? "milieu" : "derniere") : apres ? "premiere" : "seule";
    return { item, position, debutDeGroupe: !avec, finDeGroupe: !apres };
  });
}
